import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import images from "../../images";
import Lottie from "lottie-react";

import { useSwipeable } from "react-swipeable";
import { useTranslation, Trans } from "react-i18next";
import { useUpdateUserInfoMutation } from "../../services/apiSlice";
import { useDispatch } from "react-redux";
import { setHideWidget } from "../../redux/featuresSlice/commonSlice";
import { renderWithBold } from "../../Utils/helper";

const OnboardingSteps = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(1);
  const questId = searchParams.get("quest_id");
  const locationId = searchParams.get("location_id");
  const referralCode = searchParams.get("code");

  const [updateUserInfo, { isLoading }] = useUpdateUserInfoMutation();
  const [formData, setFormData] = useState({
    age: "",
    gender: "",
  });
  const [errors, setErrors] = useState({
    age: "",
    gender: "",
  });

  const stepOnboard = [
    {
      title: (
        <Trans i18nKey="onBoard.earn100Coins">
          <img
            src={images.RallyCoins}
            alt="coins"
            className="inline-block w-[30px] h-[30px] mb-[6px] max-[380px]:w-[24px] max-[380px]:h-[24px] max-[380px]:mb-[4px]"
          />
        </Trans>
      ),
      description: (
        <span
          dangerouslySetInnerHTML={{
            __html: t("onBoard.getCashbackInExchange"),
          }}
        />
      ),
      animationData: require("./../../images/animation/earn-while-dine.json"),
    },
    {
      title: t("onBoard.levelUpXPForMoreCash"),
      description: t("onBoard.collectXPByCompleting"),
      animationData: require("./../../images/animation/rocket.json"),
    },
    {
      title: t("onBoard.yourAnonymous"),
      description: (
        <span>
          {renderWithBold(t("onBoard.shareYourInfo"), {
            classNameText: "text-[#253FF6]",
          })}
        </span>
      ),
      inputs: [
        {
          id: "age",
          placeholder: t("onBoard.age"),
          type: "number",
          icon: images.CalendarIcon2,
        },
        {
          id: "gender",
          placeholder: t("onBoard.gender"),
          type: "select",
          icon: images.User3Icon,
          options: [
            { value: "", label: t("signUp.gender.gender") },
            { value: "male", label: t("signUp.gender.male") },
            { value: "female", label: t("signUp.gender.female") },
            {
              value: "maleIdentifying",
              label: t("signUp.gender.maleIdentifying"),
            },
            {
              value: "femaleIdentifying",
              label: t("signUp.gender.femaleIdentifying"),
            },
            { value: "nonBinary", label: t("signUp.gender.nonBinary") },
            { value: "agender", label: t("signUp.gender.agender") },
            {
              value: "preferNotToSay",
              label: t("signUp.gender.preferNotToSay"),
            },
          ],
        },
      ],
      buttonText: t("onBoard.confirmDetails"),
      animationData: null,
    },
  ];

  useEffect(() => {
    if (activeStep > stepOnboard.length) {
      if (locationId !== ("null" || null)) {
        navigate(`/quest/location/${locationId}`);
      } else {
        navigate(questId && questId !== "null" ? `/quest/details/${questId}` : "/");
      }
    }
  }, [activeStep, navigate, questId, locationId, stepOnboard.length]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const CustomSelect = ({ options, value, onChange, placeholder, icon }) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (selectRef.current && !selectRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const handleSelect = (optionValue) => {
      onChange(optionValue);
      setIsOpen(false);
    };

    const selectedOption = options.find((opt) => opt.value === value);

    return (
      <div className="relative w-full" ref={selectRef}>
        <div
          className="flex items-center justify-between w-full rounded-lg cursor-pointer px-4 py-1 text-base max-[380px]:text-sm"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span
            className={`flex-1 text-base max-[380px]:text-sm ${
              !value ? "text-gray-400" : "text-black"
            }`}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <img
            src={images.ChevronDownIcon}
            alt=""
            className="w-5 h-5 max-[380px]:w-4 max-[380px]:h-4"
          />
        </div>
        {isOpen && (
          <div className="absolute top-full left-0 right-0 z-50 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto mt-1">
            {options.map((option) => (
              <div
                key={option.value}
                className={`px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors text-base max-[380px]:text-sm ${
                  option.value === value ? "bg-blue-50 text-blue-600" : "text-gray-700"
                }`}
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.age || formData.age.trim() === "") {
      newErrors.age = t("onBoard.ageRequired") || "Age is required";
    } else if (parseInt(formData.age) < 13 || parseInt(formData.age) > 120) {
      newErrors.age = t("onBoard.ageInvalid");
    }

    if (!formData.gender || formData.gender === "") {
      newErrors.gender = t("onBoard.genderRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (activeStep === 2) dispatch(setHideWidget(false));
    if (activeStep === 3) {
      dispatch(setHideWidget(false));

      if (!validateForm()) {
        return;
      }

      try {
        const userData = {
          age: parseInt(formData.age),
          gender: formData.gender,
        };

        await updateUserInfo(userData).unwrap();
        const appendCode = (url) => {
          if (!referralCode) return url;
          return url.includes("?") ? `${url}&code=${referralCode}` : `${url}?code=${referralCode}`;
        };
        if (locationId !== ("null" || null)) {
          navigate(appendCode(`/quest/location/${locationId}`));
        } else {
          navigate(
            questId && questId !== "null" ? appendCode(`/quest/details/${questId}`) : "/quest",
          );
        }
      } catch (error) {
        console.error("Error updating user info:", error);
      }
    } else {
      if (activeStep >= stepOnboard.length) {
      } else {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handlePrev = () => {
    if (activeStep > 1) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (activeStep < stepOnboard.length) {
        setActiveStep((prev) => prev + 1);
      }
    },
    onSwipedRight: () => {
      if (activeStep > 1) {
        setActiveStep((prev) => prev - 1);
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  const NavigationButtons = ({ activeStep, onNext, onPrev }) => (
    <div className="flex items-center justify-between w-full">
      <div className="w-[42px] h-[42px] flex items-center justify-center">
        {activeStep > 1 && (
          <div className="w-full h-full flex items-center justify-center transition-all duration-300">
            <span onClick={onPrev} className={`icon icon-arrow-left text-[#253FF6] text-[18px]`} />
          </div>
        )}
      </div>

      <div className="w-[42px] h-[42px] flex items-center justify-center">
        {activeStep < 3 && (
          <div className="w-full h-full flex items-center justify-center transition-all duration-300">
            <span onClick={onNext} className={`icon icon-arrow-right text-[#253FF6] text-[18px]`} />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <main
      {...swipeHandlers}
      className="relative flex flex-col mx-auto w-full h-[100dvh] overflow-y-auto hide-scrollbar bg-white px-10 max-xs:py-9 py-[72px] max-xs:gap-[30px] gap-[60px] 2xs:max-w-[430px]"
    >
      <Helmet>
        <meta name="theme-color" content="#ffffff" />
      </Helmet>

      <div className="flex flex-col flex-1 h-full gap-[30px] xs:gap-[60px]">
        <section className="flex w-full">
          <img
            src={images.LogoLogin}
            className="w-[80px] h-[60px] aspect-[4/3] max-[380px]:w-[64px] max-[380px]:h-[48px]"
            alt="Logo"
          />
        </section>

        <section className="flex flex-col flex-1 gap-[0] xs:gap-[30px] justify-start">
          <div className="flex flex-col gap-[30px] xs:gap-[60px]">
            <h1 className="text-[#253FF6] text-[24px] xs:text-[36px] leading-[2.5rem] font-bold whitespace-pre-line">
              {stepOnboard[activeStep - 1]?.title}
            </h1>
            <p className="text-[14px] xs:text-[20px] font-medium text-[#253FF6]">
              {stepOnboard[activeStep - 1]?.description}
            </p>
          </div>

          {activeStep === 3 ? (
            <div className="flex flex-col gap-4 mt-4 flex-1">
              <div className="flex flex-col gap-4">
                {stepOnboard[2]?.inputs.map((input) => (
                  <div key={input.id} className="flex flex-col">
                    <div className="flex items-center border border-gray-300 rounded-lg px-4 py-3 max-[380px]:px-3 max-[380px]:py-2">
                      <img
                        src={input.icon}
                        alt="Icon"
                        className="w-5 h-5 mr-3 max-[380px]:w-4 max-[380px]:h-4 max-[380px]:mr-2"
                      />
                      {input.type === "select" ? (
                        <CustomSelect
                          options={input.options}
                          value={formData[input.id]}
                          onChange={(value) => handleInputChange(input.id, value)}
                          placeholder={input.placeholder}
                          icon={input.icon}
                        />
                      ) : (
                        <input
                          id={input.id}
                          type={input.type}
                          placeholder={input.placeholder}
                          value={formData[input.id]}
                          onChange={(e) => handleInputChange(input.id, e.target.value)}
                          className="w-full px-4 py-1 text-base max-[380px]:text-sm max-[380px]:px-3 max-[380px]:py-0.5 focus:outline-none focus:border-transparent"
                          min={input.type === "number" ? "13" : undefined}
                          max={input.type === "number" ? "120" : undefined}
                        />
                      )}
                    </div>

                    {errors[input.id] && (
                      <p className="text-red-500 text-sm mt-1 ml-1 max-[380px]:text-xs">
                        {errors[input.id]}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-auto pb-4">
                <button
                  className={`w-full px-4 py-3 text-white rounded-lg text-lg font-medium transition-all max-[380px]:text-base max-[380px]:py-2 ${
                    isLoading ? "bg-gray-400 cursor-not-allowed" : "bg-[#253FF6] hover:bg-[#1e32cc]"
                  }`}
                  onClick={handleNext}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin max-[380px]:w-4 max-[380px]:h-4"></div>
                      {t("base.loading")}
                    </div>
                  ) : (
                    stepOnboard[2]?.buttonText
                  )}
                </button>
              </div>
            </div>
          ) : (
            <figure className="flex justify-center items-center w-full mt-10">
              {stepOnboard[activeStep - 1]?.animationData ? (
                <div className="w-[250px] xs:w-[300px]">
                  <Lottie
                    animationData={stepOnboard[activeStep - 1].animationData}
                    loop={false}
                    autoplay={true}
                  />
                </div>
              ) : (
                <div className="w-[200px] h-[250px]"></div>
              )}
            </figure>
          )}

          {activeStep !== 3 && (
            <div className="w-full z-10 mt-20 pb-10">
              <NavigationButtons activeStep={activeStep} onNext={handleNext} onPrev={handlePrev} />
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default OnboardingSteps;
